<template>
    <DemoToolsLauncher title="My Work demo tools">
        <div class="flex flex-col gap-4">
            <h2 class="body-small-bold text-valence-stone-900">My Work demo tools</h2>

            <div class="fill-section">
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_insight_feed_flag'"
                    @click="run('toggle_insight_feed_flag', TOGGLE_FLAG_URL, { flag: 'my_work_insight_feed' })">
                    <i :class="hasInsightFeedFlag ? 'bi bi-flag-fill' : 'bi bi-flag'" class="shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasInsightFeedFlag ? t("myWorkInsightFeed.demoFlagOff") : t("myWorkInsightFeed.demoFlagOn") }}</span>
                    <i v-if="busyAction === 'toggle_insight_feed_flag'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
            </div>

            <div class="fill-section">
                <h3 class="body-small-bold text-valence-stone-900">Calendar</h3>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_calendar'"
                    @click="run('toggle_calendar', TOGGLE_CALENDAR_URL)">
                    <i :class="hasIntegratedCalendar ? 'bi bi-calendar-x' : 'bi bi-calendar-check'" class="shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasIntegratedCalendar ? "Disconnect calendar" : "Connect calendar" }}</span>
                    <i v-if="busyAction === 'toggle_calendar'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
            </div>

            <div class="fill-section">
                <h3 class="body-small-bold text-valence-stone-900">Insights</h3>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'show_recent_insights'"
                    @click="run('show_recent_insights', SHOW_RECENT_INSIGHTS_URL)">
                    <i class="bi bi-eye shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">Show 3 insights</span>
                    <i v-if="busyAction === 'show_recent_insights'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'hide_all_insights'"
                    @click="run('hide_all_insights', HIDE_ALL_INSIGHTS_URL)">
                    <i class="bi bi-eye-slash shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">Hide all insights</span>
                    <i v-if="busyAction === 'hide_all_insights'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'clear_feedback'"
                    @click="run('clear_feedback', CLEAR_FEEDBACK_URL)">
                    <i class="bi bi-eraser shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">Clear Insight Feedback</span>
                    <i v-if="busyAction === 'clear_feedback'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton theme="neutral" size="sm" class="tool-row" :disabled="busyAction !== null" @click="showDatePicker = !showDatePicker">
                    <i class="bi bi-calendar-date shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">Set insights date</span>
                    <i :class="showDatePicker ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi shrink-0" aria-hidden="true"></i>
                </BaseButton>
                <div v-if="showDatePicker" class="date-picker-row">
                    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
                    <label for="mywork-demo-shift-date" class="sr-only">Date to shift live insights to</label>
                    <input id="mywork-demo-shift-date" v-model="shiftDate" type="date" class="date-input" :disabled="busyAction !== null" />
                    <BaseButton
                        theme="primary"
                        size="sm"
                        class="shrink-0"
                        :disabled="busyAction !== null || !shiftDate"
                        :aria-busy="busyAction === 'shift_insight_dates'"
                        @click="run('shift_insight_dates', SHIFT_INSIGHT_DATES_URL, { target_date: shiftDate })">
                        Apply
                        <i v-if="busyAction === 'shift_insight_dates'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                    </BaseButton>
                </div>
            </div>

            <div class="fill-section">
                <h3 class="body-small-bold text-valence-stone-900">Context flags</h3>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_perspective_flag'"
                    @click="run('toggle_perspective_flag', TOGGLE_FLAG_URL, { flag: 'perspective_assessment' })">
                    <i :class="hasPerspectiveFlag ? 'bi bi-flag-fill' : 'bi bi-flag'" class="shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasPerspectiveFlag ? "Turn Perspective assessment flag off" : "Turn Perspective assessment flag on" }}</span>
                    <i v-if="busyAction === 'toggle_perspective_flag'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_perspective_report_flag'"
                    @click="run('toggle_perspective_report_flag', TOGGLE_FLAG_URL, { flag: 'perspectives_report' })">
                    <i :class="hasPerspectiveReportFlag ? 'bi bi-flag-fill' : 'bi bi-flag'" class="shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasPerspectiveReportFlag ? "Turn Perspective report flag off" : "Turn Perspective report flag on" }}</span>
                    <i v-if="busyAction === 'toggle_perspective_report_flag'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <p class="details text-valence-stone-500">Turning one of these on turns the other off — they're mutually exclusive.</p>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_public_profile_flag'"
                    @click="run('toggle_public_profile_flag', TOGGLE_FLAG_URL, { flag: 'public_profile' })">
                    <i :class="hasPublicProfileFlag ? 'bi bi-flag-fill' : 'bi bi-flag'" class="shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasPublicProfileFlag ? "Turn collaboration profile flag off" : "Turn collaboration profile flag on" }}</span>
                    <i v-if="busyAction === 'toggle_public_profile_flag'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
            </div>

            <div class="fill-section">
                <h3 class="body-small-bold text-valence-stone-900">Perspective &amp; collaboration profile</h3>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_perspective_result'"
                    @click="run('toggle_perspective_result', TOGGLE_PERSPECTIVE_RESULT_URL)">
                    <i class="bi bi-stars shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasPerspective ? "Delete Perspective result" : "Complete a Perspective" }}</span>
                    <i v-if="busyAction === 'toggle_perspective_result'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_perspective_report'"
                    @click="run('toggle_perspective_report', TOGGLE_PERSPECTIVE_REPORT_URL)">
                    <i class="bi bi-file-earmark-text shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasPerspectiveReport ? "Delete Perspective report" : "Create Perspective report" }}</span>
                    <i v-if="busyAction === 'toggle_perspective_report'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <p class="details text-valence-stone-500">A different DB object from a Perspective result — only read when the report flag above is on.</p>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'toggle_collaboration_profile'"
                    @click="run('toggle_collaboration_profile', TOGGLE_COLLABORATION_PROFILE_URL)">
                    <i class="bi bi-person-badge shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ hasCollaborationProfile ? "Clear collaboration profile" : "Create collaboration profile" }}</span>
                    <i v-if="busyAction === 'toggle_collaboration_profile'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
            </div>

            <div class="fill-section">
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="tool-row"
                    :disabled="busyAction !== null"
                    :aria-busy="busyAction === 'reset_insight_feed'"
                    @click="run('reset_insight_feed', RESET_INSIGHT_FEED_URL)">
                    <i class="bi bi-arrow-counterclockwise shrink-0" aria-hidden="true"></i>
                    <span class="grow truncate text-left">{{ t("myWorkInsightFeed.demoReset") }}</span>
                    <i v-if="busyAction === 'reset_insight_feed'" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                </BaseButton>
                <p class="details text-ink-muted">{{ t("myWorkInsightFeed.demoResetDescription") }}</p>
            </div>

            <p v-if="inlineError" class="details text-status-error" role="alert">{{ inlineError }}</p>
        </div>
    </DemoToolsLauncher>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import DemoToolsLauncher from "~vue/DemoTools/DemoToolsLauncher.vue";
import { demoToolsErrorMessage } from "~vue/DemoTools/errorMessage";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    hasInsightFeedFlag?: boolean;
    hasIntegratedCalendar?: boolean;
    hasPerspective?: boolean;
    hasPerspectiveReport?: boolean;
    hasCollaborationProfile?: boolean;
    hasPerspectiveFlag?: boolean;
    hasPerspectiveReportFlag?: boolean;
    hasPublicProfileFlag?: boolean;
}

withDefaults(defineProps<Props>(), {
    hasInsightFeedFlag: false,
    hasIntegratedCalendar: false,
    hasPerspective: false,
    hasPerspectiveReport: false,
    hasCollaborationProfile: false,
    hasPerspectiveFlag: false,
    hasPerspectiveReportFlag: false,
    hasPublicProfileFlag: false,
});

// Static coach_api paths, called directly from the browser — mirrors PerspectiveDemoTools.vue's
// own convention. Staff gating is enforced by DemoToolsLauncher (client-side visibility) and by
// each coach_api endpoint itself (401 for non-staff), so there's no vpoc involvement needed here.
const TOGGLE_CALENDAR_URL = "/v1/mywork/demo/toggle-calendar";
const RESET_INSIGHT_FEED_URL = "/v1/mywork/demo/reset-insight-feed";
const SHOW_RECENT_INSIGHTS_URL = "/v1/mywork/demo/show-recent-insights";
const HIDE_ALL_INSIGHTS_URL = "/v1/mywork/demo/hide-all-insights";
const CLEAR_FEEDBACK_URL = "/v1/mywork/demo/clear-feedback";
const SHIFT_INSIGHT_DATES_URL = "/v1/mywork/demo/shift-insight-dates";
const TOGGLE_FLAG_URL = "/v1/mywork/demo/toggle-flag";
const TOGGLE_PERSPECTIVE_RESULT_URL = "/v1/mywork/demo/toggle-perspective-result";
const TOGGLE_PERSPECTIVE_REPORT_URL = "/v1/mywork/demo/toggle-perspective-report";
const TOGGLE_COLLABORATION_PROFILE_URL = "/v1/mywork/demo/toggle-collaboration-profile";

const { t } = useI18n();

const busyAction = ref<string | null>(null);
const inlineError = ref<string | null>(null);
const showDatePicker = ref(false);
const shiftDate = ref(new Date().toISOString().slice(0, 10));

async function run(action: string, url: string, body?: Record<string, unknown>): Promise<void> {
    if (busyAction.value !== null) {
        return;
    }
    busyAction.value = action;
    inlineError.value = null;

    const request = body ? useFetch(url).post(body) : useFetch(url).post();
    const { error, statusCode } = await request;
    busyAction.value = null;

    if (error.value) {
        inlineError.value = demoToolsErrorMessage(statusCode.value, error.value.message, "Could not run this demo tool.");
        return;
    }

    globalThis.location.reload();
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .tool-row {
        @apply flex w-full items-center gap-2 overflow-hidden text-left;
    }

    .fill-section {
        @apply flex flex-col gap-2 border-t border-valence-cream-200 pt-3;
    }

    .fill-section:first-of-type {
        @apply border-t-0 pt-0;
    }

    .date-picker-row {
        @apply flex items-center gap-2 pl-6;
    }

    .date-input {
        @apply w-full rounded-md border border-control-border bg-control-bg p-2 transition-colors scaffold-body-small text-control-text-strong focus-field;
    }

    .spin {
        animation: mywork-demo-tools-spin 1s linear infinite;
    }
}

@keyframes mywork-demo-tools-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .spin {
        animation: none;
    }
}
</style>
